import { marginRate, type MarginBasis } from "@erp/shared";
import { useQuery } from "@tanstack/react-query";
import { companyQuery } from "~/features/sales/send-email";
import { useCan } from "~/lib/session";
import { useT } from "~/lib/i18n";

/**
 * The margin ratio as the company chose to read it (Settings › Quotes and invoices): « taux de
 * marque » (margin ÷ selling price, the default) or « taux de marge » (margin ÷ purchase cost).
 */
export function useMarginRate() {
  const t = useT();
  const can = useCan();
  const company = useQuery({ ...companyQuery, enabled: can("sales:read") });
  const basis: MarginBasis = company.data?.marginBasis ?? "sale";
  return {
    basis,
    /** « Taux de marque » or « Taux de marge ». */
    label: t(`margin.label.${basis}`),
    /** The ratio of a margin and the cost it was made on; null when its base is zero. */
    rate: (marginCents: number, costCents: number) => marginRate(marginCents, costCents, basis),
    /** A ratio computed on the selling price (the API's `marginRatio`), read in the chosen basis. */
    fromSaleRatio: (ratio: number | null) =>
      ratio === null || basis === "sale" ? ratio : ratio < 1 ? ratio / (1 - ratio) : null,
  };
}
